/**
 * Abertura do banco e aplicação das migrações.
 *
 * No navegador é o mesmo SQLite do app, compilado para WebAssembly e gravando
 * no armazenamento privado da origem (OPFS) — então `esquema.ts` e as 800
 * linhas de SQL do `repositorio.ts` valem sem mudança. A diferença é que ele
 * roda num worker (ver `banco.worker.ts`), e cada `execute` vira uma mensagem.
 *
 * O que o navegador acrescenta de risco, e onde está a defesa:
 *
 * - Duas abas no mesmo banco: o SQLite do worker só aceita um dono. A aba que
 *   chega depois não quebra — ela oferece "usar aqui", e a que tinha o banco
 *   termina o que está gravando, fecha e passa a posse (como o WhatsApp Web).
 * - Faxina do navegador: sem pedido de armazenamento persistente, o navegador
 *   pode apagar os dados do site para liberar espaço — e com eles validação
 *   que ainda não subiu. O pedido é feito aqui; quem decide é o navegador.
 */

import {MIGRACOES} from './esquema';

type Linha = Record<string, unknown>;

export interface ResultadoConsulta {
  rows: Linha[];
}

/** O pedaço da interface do op-sqlite que o repositório usa. */
export interface DB {
  execute(sql: string, params?: unknown[]): Promise<ResultadoConsulta>;
}

/** O banco está com outra aba deste navegador. */
export class BancoEmOutraAba extends Error {
  constructor(message = 'O validador está aberto em outra aba deste navegador.') {
    super(message);
    this.name = 'BancoEmOutraAba';
  }
}

type Resposta = {id: number; rows?: Linha[]; erro?: string};

const TRAVA = 'tmobi-validador-banco';
const CANAL = 'tmobi-validador-banco';

/** Quanto a aba nova espera a outra soltar o banco antes de desistir. */
const ESPERA_PELA_OUTRA_ABA_MS = 8000;

let trabalhador: Worker | null = null;
let proximoId = 1;
const esperando = new Map<number, {resolver: (r: ResultadoConsulta) => void; rejeitar: (e: Error) => void}>();

/** Solta a trava do navegador. Existe só enquanto esta aba é a dona. */
let soltarTrava: (() => void) | null = null;
let canal: BroadcastChannel | null = null;
/** Passou a posse para outra aba: consulta nova é recusada. */
let cedido = false;
const aoPerderPosse = new Set<() => void>();

function iniciarTrabalhador(): Worker {
  const w = new Worker(new URL('./banco.worker.ts', import.meta.url), {type: 'module'});

  w.onmessage = ({data}: MessageEvent<Resposta>) => {
    const pedido = esperando.get(data.id);
    if (!pedido) {
      return;
    }
    esperando.delete(data.id);
    if (data.erro !== undefined) {
      pedido.rejeitar(new Error(data.erro));
    } else {
      pedido.resolver({rows: data.rows ?? []});
    }
  };

  // Worker que nem sobe (navegador sem módulo em worker, wasm bloqueado): cada
  // consulta pendente recebe o erro, em vez de esperar para sempre.
  w.onerror = evento => {
    const erro = new Error(`O banco local não abriu: ${evento.message || 'erro no worker'}`);
    esperando.forEach(p => p.rejeitar(erro));
    esperando.clear();
  };

  return w;
}

function enviar(mensagem: Record<string, unknown>): Promise<ResultadoConsulta> {
  trabalhador ??= iniciarTrabalhador();
  const id = proximoId++;
  return new Promise((resolver, rejeitar) => {
    esperando.set(id, {resolver, rejeitar});
    trabalhador!.postMessage({id, ...mensagem});
  });
}

const instancia: DB = {
  execute(sql, params = []) {
    if (cedido) {
      return Promise.reject(new BancoEmOutraAba());
    }
    // `undefined` não é valor de SQL; o op-sqlite também o trata como null.
    return enviar({sql, params: params.map(p => (p === undefined ? null : p))});
  },
};

export function banco(): DB {
  return instancia;
}

/**
 * Outra aba pediu o banco. Nada do que já foi pedido se perde: as consultas
 * em andamento terminam, o banco fecha direito, e só então a trava é solta.
 * A tela desta aba é avisada para sair do caminho (ver App.tsx).
 */
async function cederPosse(): Promise<void> {
  if (!soltarTrava) {
    return;
  }
  console.log('[banco] outra aba pediu o banco; cedendo');
  cedido = true;
  aoPerderPosse.forEach(avisar => avisar());

  const limite = Date.now() + 5000;
  while (esperando.size > 0 && Date.now() < limite) {
    await new Promise(r => setTimeout(r, 20));
  }
  try {
    await enviar({fechar: true});
  } catch (e) {
    console.warn(`[banco] fechamento antes de ceder falhou: ${e}`);
  }
  trabalhador?.terminate();
  trabalhador = null;

  canal?.close();
  canal = null;
  soltarTrava();
  soltarTrava = null;
}

/**
 * Segura a posse do banco enquanto a aba estiver aberta. A trava do navegador
 * é solta sozinha quando a aba fecha ou recarrega — não há o que limpar.
 *
 * `esperar` é o "usar aqui": em vez de desistir na hora, espera a outra aba
 * ceder, até um prazo.
 */
function tomarPosse(esperar: boolean): Promise<void> {
  if (soltarTrava || !navigator.locks) {
    return Promise.resolve();
  }
  return new Promise((resolver, rejeitar) => {
    const opcoes: LockOptions = esperar
      ? {signal: AbortSignal.timeout(ESPERA_PELA_OUTRA_ABA_MS)}
      : {ifAvailable: true};

    navigator.locks
      .request(TRAVA, opcoes, trava => {
        if (!trava) {
          rejeitar(new BancoEmOutraAba());
          return;
        }
        cedido = false;
        canal = new BroadcastChannel(CANAL);
        canal.onmessage = ({data}) => {
          if (data === 'assumir') {
            cederPosse();
          }
        };
        resolver();
        return new Promise<void>(soltar => {
          soltarTrava = soltar;
        });
      })
      .catch(e => {
        // Prazo estourado: a outra aba não respondeu (congelada pelo
        // navegador, ou travada). Não há como tirar o banco dela à força.
        rejeitar(
          (e as Error)?.name === 'TimeoutError'
            ? new BancoEmOutraAba(
                'A outra aba não respondeu. Feche-a (ou recarregue) e tente de novo aqui.',
              )
            : e,
        );
      });
  });
}

/**
 * "Usar aqui": pede o banco à aba que o tem e espera ela ceder. Depois disso
 * a abertura segue normal, por `prepararBanco`.
 */
export async function assumirBanco(): Promise<void> {
  const pedido = new BroadcastChannel(CANAL);
  pedido.postMessage('assumir');
  pedido.close();
  await tomarPosse(true);
}

/** Avisa quando esta aba perde o banco para outra. Devolve o cancelamento. */
export function quandoPerderPosse(avisar: () => void): () => void {
  aoPerderPosse.add(avisar);
  return () => aoPerderPosse.delete(avisar);
}

async function pedirArmazenamentoPersistente(): Promise<void> {
  try {
    const persistente =
      (await navigator.storage?.persisted?.()) || (await navigator.storage?.persist?.());
    console.log(`[banco] armazenamento persistente: ${persistente ? 'sim' : 'não'}`);
  } catch (e) {
    console.warn(`[banco] pedido de armazenamento persistente falhou: ${e}`);
  }
}

/** Cria as tabelas que faltam e sobe a versão do esquema. Idempotente. */
export async function prepararBanco(): Promise<void> {
  await tomarPosse(false);
  await pedirArmazenamentoPersistente();

  const db = banco();

  // Sem `journal_mode = WAL`, que o app nativo usa: o VFS do OPFS não tem a
  // memória compartilhada que o WAL exige.
  await db.execute('pragma foreign_keys = ON');

  const {rows} = await db.execute('pragma user_version');
  const versaoAtual = Number(rows?.[0]?.user_version ?? 0);

  for (let versao = versaoAtual; versao < MIGRACOES.length; versao++) {
    for (const comando of MIGRACOES[versao]) {
      await db.execute(comando);
    }
    await db.execute(`pragma user_version = ${versao + 1}`);
  }
}

/** Helper de tipagem: as linhas vêm como `Array<Record<string, unknown>>`. */
export function linhas<T>(resultado: {rows?: Array<Record<string, unknown>>}): T[] {
  return (resultado.rows ?? []) as T[];
}

export async function primeiraLinha<T>(
  resultado: {rows?: Array<Record<string, unknown>>},
): Promise<T | null> {
  const lista = linhas<T>(resultado);
  return lista.length > 0 ? lista[0] : null;
}
